import { Action, combineReducers, createReducer, on } from '@ngrx/store'
import { reduceReducers } from '@shared/util/reduce-reducers'
import { CreditMetricConfiguration } from './../../model/credit-metric.model'
import { getTotalWeight } from '../../utils/credit.utils'
import { CreditStructureGroupFilterActions } from '../group/credit-group.actions'
import * as fromCreditMetricActions from './credit-metrics.actions'
export interface State {
  metricConfigurationNames: string[]
  metricConfiguration?: CreditMetricConfiguration
  isLoading: boolean
  isDirty: boolean
  isSaving: boolean
  isGse: boolean
}

export const initialState: State = {
  metricConfigurationNames: [],
  isLoading: false,
  isDirty: false,
  isSaving: false,
  isGse: false,
}

const metricConfigurationNamesReducer = createReducer(
  initialState.metricConfigurationNames,
  on(
    fromCreditMetricActions.GetMetricConfigurationNamesByCarrierIdSuccess,
    (_, { metricConfigurationNames }) => metricConfigurationNames,
  ),
  on(
    fromCreditMetricActions.DeleteMetricConfigurationSuccess,
    (state, { metricConfigurationName }) => state.filter((s) => s !== metricConfigurationName),
  ),
  on(fromCreditMetricActions.Reset, () => [] as string[]),
)

const metricConfigurationReducer = createReducer(
  initialState.metricConfiguration,
  on(
    fromCreditMetricActions.SelectMetricConfigurationSuccess,
    fromCreditMetricActions.CreateMetricConfigurationSuccess,
    (_, { metricConfiguration }) => metricConfiguration,
  ),
  on(
    fromCreditMetricActions.UpdateMetricConfigurationName,
    (state, { metricConfigurationName }) => {
      const metricSettings = state.metricSettings.map((m) => {
        return { ...m, name: metricConfigurationName }
      })
      return {
        ...state,
        name: metricConfigurationName,
        metricSettings,
      }
    },
  ),
  on(fromCreditMetricActions.UpdateCreditMetricSetting, (state, { metricSetting }) => {
    const metricSettings = [...state.metricSettings]
    const metricSettingIndex = metricSettings.findIndex(
      (s) => s.category === metricSetting.category && s.ordinal === metricSetting.ordinal,
    )
    if (metricSettingIndex >= 0) {
      metricSettings.splice(metricSettingIndex, 1, metricSetting)
    }
    return {
      ...state,
      metricSettings,
      weight: getTotalWeight(metricSettings),
    }
  }),
  on(fromCreditMetricActions.AddCreditMetricSetting, (state, { metricSetting }) => {
    const metricSettings = [...state.metricSettings, metricSetting]
    return {
      ...state,
      metricSettings,
      weight: getTotalWeight(metricSettings),
    }
  }),
  on(fromCreditMetricActions.DeleteCreditMetricSettingSuccess, (state, { metricSetting }) => {
    const metricSettings = state.metricSettings.filter((s) => s.id !== metricSetting.id)
    return {
      ...state,
      metricSettings,
      weight: getTotalWeight(metricSettings),
    }
  }),
  on(fromCreditMetricActions.EnableAllCreditMetricSettings, (state) => {
    const metricSettings = state.metricSettings.map((s) => {
      return { ...s, show: true }
    })
    return {
      ...state,
      metricSettings,
      weight: getTotalWeight(metricSettings),
    }
  }),
  on(fromCreditMetricActions.DisableAllCreditMetricSettings, (state) => {
    const metricSettings = state.metricSettings.map((s) => {
      return { ...s, show: false }
    })
    return {
      ...state,
      metricSettings,
      weight: getTotalWeight(metricSettings),
    }
  }),
  on(fromCreditMetricActions.ReorderCreditMetricSettings, (state, { metricSettings }) => {
    const category = metricSettings[0]?.category
    const otherSettings = state.metricSettings.filter((s) => s.category !== category)
    return {
      ...state,
      metricSettings: [...otherSettings, ...metricSettings],
    }
  }),
  on(fromCreditMetricActions.SaveCreditMetricSettingsSuccess, (state, { metricSettings }) => {
    return { ...state, metricSettings }
  }),
  on(
    fromCreditMetricActions.Reset,
    CreditStructureGroupFilterActions.reset,
    fromCreditMetricActions.DeleteMetricConfigurationSuccess,
    (_) => undefined,
  ),
)

const isLoadingReducer = createReducer(
  initialState.isLoading,
  on(fromCreditMetricActions.SelectMetricConfiguration, () => true),
  on(
    fromCreditMetricActions.SelectMetricConfigurationSuccess,
    fromCreditMetricActions.SelectMetricConfigurationFailure,
    fromCreditMetricActions.CreateMetricConfigurationSuccess,
    fromCreditMetricActions.Reset,
    () => false,
  ),
)

const isDirtyReducer = createReducer(
  initialState.isDirty,
  on(
    fromCreditMetricActions.CreateMetricConfigurationSuccess,
    fromCreditMetricActions.UpdateMetricConfigurationName,
    fromCreditMetricActions.UpdateCreditMetricSetting,
    fromCreditMetricActions.AddCreditMetricSetting,
    fromCreditMetricActions.EnableAllCreditMetricSettings,
    fromCreditMetricActions.DisableAllCreditMetricSettings,
    fromCreditMetricActions.ReorderCreditMetricSettings,
    () => true,
  ),
  on(
    fromCreditMetricActions.SaveCreditMetricSettingsSuccess,
    fromCreditMetricActions.Reset,
    fromCreditMetricActions.DeleteMetricConfigurationSuccess,
    () => false,
  ),
)

const isSavingReducer = createReducer(
  initialState.isSaving,
  on(fromCreditMetricActions.SaveCreditMetricSettings, () => true),
  on(
    fromCreditMetricActions.SaveCreditMetricSettingsSuccess,
    fromCreditMetricActions.SaveCreditMetricSettingsFailure,
    fromCreditMetricActions.Reset,
    () => false,
  ),
)

const isGseReducer = createReducer(
  initialState.isGse,
  on(fromCreditMetricActions.GetMetricConfigurationNamesByCarrierId, (_, { isGse }) => isGse),
  on(fromCreditMetricActions.Reset, () => false),
)

const creditMetricsReducer = combineReducers<State>({
  metricConfigurationNames: metricConfigurationNamesReducer,
  metricConfiguration: metricConfigurationReducer,
  isLoading: isLoadingReducer,
  isDirty: isDirtyReducer,
  isSaving: isSavingReducer,
  isGse: isGseReducer,
})

const reducedReducer = reduceReducers(creditMetricsReducer)

export function reducer(state: State | undefined, action: Action): State {
  return reducedReducer(state, action)
}
